import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import {
    getProducts,
    subscribeProducts
} from "../services/ProductService";

import pb from "../../lib/pb";


export default function BrandMarketplace({ brand }) {

    /*
    --------------------------------------------------
    STATE
    --------------------------------------------------
    */

    const [products, setProducts] = useState([]);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState(null);

    const [search, setSearch] = useState("");

    const [sort, setSort] = useState("newest");


    /*
    --------------------------------------------------
    LOAD PRODUCTS
    --------------------------------------------------
    */

    useEffect(() => {

        if (!brand?.id) {

            setProducts([]);

            setLoading(false);

            return;

        }

        let active = true;


        const loadProducts = async () => {

            try {

                setLoading(true);

                const records =
                    await getProducts(brand.id);


                if (active) {

                    /*
                    Marketplace only shows products
                    which are actually published and
                    publicly visible.
                    */

                    const marketplaceProducts =
                        records.filter(product =>

                            product.status === "published" &&

                            product.visibility === "public"

                        );


                    setProducts(
                        marketplaceProducts
                    );

                    setError(null);

                }

            }

            catch (err) {

                console.error(
                    "Failed to load marketplace products:",
                    err
                );


                if (active) {

                    setError(
                        err?.message ||
                        "Failed to load marketplace products."
                    );

                    setProducts([]);

                }

            }

            finally {

                if (active) {

                    setLoading(false);

                }

            }

        };


        loadProducts();


        /*
        --------------------------------------------------
        REAL-TIME PRODUCT SYNC
        --------------------------------------------------
        */

        let unsubscribe = null;


        const subscribe = async () => {

            try {

                unsubscribe =
                    await subscribeProducts(

                        brand.id,

                        event => {

                            if (!active) {
                                return;
                            }


                            const record =
                                event?.record;


                            if (!record) {
                                return;
                            }


                            /*
                            CREATE
                            */

                            if (
                                event.action === "create"
                            ) {

                                if (

                                    record.status === "published" &&

                                    record.visibility === "public"

                                ) {

                                    setProducts(prev => {

                                        if (
                                            prev.some(
                                                item =>
                                                    item.id === record.id
                                            )
                                        ) {

                                            return prev;

                                        }


                                        return [
                                            record,
                                            ...prev
                                        ];

                                    });

                                }

                            }


                            /*
                            UPDATE
                            */

                            if (
                                event.action === "update"
                            ) {

                                setProducts(prev => {

                                    const isMarketplaceProduct =

                                        record.status === "published" &&

                                        record.visibility === "public";


                                    /*
                                    If it is no longer eligible
                                    for Marketplace, remove it.
                                    */

                                    if (
                                        !isMarketplaceProduct
                                    ) {

                                        return prev.filter(

                                            item =>
                                                item.id !== record.id

                                        );

                                    }


                                    /*
                                    Existing product.
                                    */

                                    const exists =
                                        prev.some(

                                            item =>
                                                item.id === record.id

                                        );


                                    if (exists) {

                                        return prev.map(

                                            item =>

                                                item.id === record.id

                                                    ? record

                                                    : item

                                        );

                                    }


                                    /*
                                    Product has just become
                                    Marketplace-visible.
                                    */

                                    return [
                                        record,
                                        ...prev
                                    ];

                                });

                            }


                            /*
                            DELETE
                            */

                            if (
                                event.action === "delete"
                            ) {

                                setProducts(prev =>

                                    prev.filter(

                                        item =>
                                            item.id !== record.id

                                    )

                                );

                            }

                        }

                    );

            }

            catch (err) {

                console.error(
                    "Failed to subscribe to marketplace products:",
                    err
                );

            }

        };


        subscribe();


        return () => {

            active = false;


            if (
                typeof unsubscribe === "function"
            ) {

                unsubscribe();

            }

        };

    }, [brand?.id]);


    /*
    --------------------------------------------------
    POCKETBASE FILE URL
    --------------------------------------------------
    */

    const getProductImage = (
        product
    ) => {

        if (
            !product ||
            !product.images
        ) {

            return null;

        }


        const images =
            Array.isArray(product.images)

                ? product.images

                : [product.images];


        if (!images.length) {

            return null;

        }


        const filename =
            images[0];


        if (!filename) {

            return null;

        }


        try {

            return pb
                .files
                .getURL(
                    product,
                    filename
                );

        }

        catch (err) {

            console.error(
                "Failed to generate product image URL:",
                err
            );

            return null;

        }

    };


    /*
    --------------------------------------------------
    CURRENCY FORMATTER
    --------------------------------------------------
    */

    const formatPrice = (
        price,
        currency = "USD"
    ) => {

        if (
            price === null ||
            price === undefined ||
            price === ""
        ) {

            return "—";

        }


        try {

            return new Intl.NumberFormat(
                undefined,
                {
                    style: "currency",
                    currency:
                        currency || "USD"
                }
            ).format(
                Number(price)
            );

        }

        catch {

            return `${currency || ""} ${price}`;

        }

    };


    /*
    --------------------------------------------------
    FILTER + SORT
    --------------------------------------------------
    */

    const filteredProducts =
        useMemo(() => {

            let result = [
                ...products
            ];


            /*
            Search
            */

            const query =
                search
                    .trim()
                    .toLowerCase();


            if (query) {

                result =
                    result.filter(
                        product => {

                            const title =
                                String(
                                    product.title || ""
                                ).toLowerCase();


                            const description =
                                String(
                                    product.shortDescription || ""
                                ).toLowerCase();


                            const category =
                                String(
                                    product.category || ""
                                ).toLowerCase();


                            const sku =
                                String(
                                    product.sku || ""
                                ).toLowerCase();


                            const tags =
                                Array.isArray(
                                    product.tags
                                )

                                    ? product.tags
                                        .join(" ")
                                        .toLowerCase()

                                    : String(
                                        product.tags || ""
                                    ).toLowerCase();


                            return (

                                title.includes(query) ||

                                description.includes(query) ||

                                category.includes(query) ||

                                sku.includes(query) ||

                                tags.includes(query)

                            );

                        }
                    );

            }


            /*
            Sort
            */

            if (
                sort === "newest"
            ) {

                result.sort(
                    (a, b) =>

                        new Date(
                            b.created || 0
                        ) -

                        new Date(
                            a.created || 0
                        )

                );

            }


            if (
                sort === "popular"
            ) {

                result.sort(
                    (a, b) =>

                        Number(
                            b.views || 0
                        ) -

                        Number(
                            a.views || 0
                        )

                );

            }


            if (
                sort === "highest"
            ) {

                result.sort(
                    (a, b) =>

                        Number(
                            b.price || 0
                        ) -

                        Number(
                            a.price || 0
                        )

                );

            }


            if (
                sort === "lowest"
            ) {

                result.sort(
                    (a, b) =>

                        Number(
                            a.price || 0
                        ) -

                        Number(
                            b.price || 0
                        )

                );

            }


            return result;

        }, [
            products,
            search,
            sort
        ]);


    /*
    --------------------------------------------------
    MARKETPLACE STATISTICS
    --------------------------------------------------
    */

    const totalViews =
        products.reduce(

            (total, product) =>

                total +
                Number(
                    product.views || 0
                ),

            0

        );


    const totalSold =
        products.reduce(

            (total, product) =>

                total +
                Number(
                    product.sold || 0
                ),

            0

        );


    const featuredCount =
        products.filter(
            product =>
                product.featured
        ).length;


    /*
    --------------------------------------------------
    ADD PRODUCT
    --------------------------------------------------
    */

    const handleAddProduct = () => {

        /*
        BrandProducts is the actual product
        management screen.
        */

        window.location.href =
            "/brand/products";

    };


    /*
    --------------------------------------------------
    VIEW PRODUCT
    --------------------------------------------------
    */

    const handleViewProduct = (
        product
    ) => {

        /*
        Keep this ready for the product-detail
        route we will build next.
        */

        window.location.href =
            `/brand/marketplace/${product.id}`;

    };


    /*
    --------------------------------------------------
    RENDER
    --------------------------------------------------
    */

    return (

        <div>

            {/* ==================================================
                HERO
            ================================================== */}

            <div
                style={{
                    background: "#1f2937",
                    color: "#fff",
                    borderRadius: 20,
                    padding: "45px 50px",
                    marginBottom: 30,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 30,
                    flexWrap: "wrap"
                }}
            >

                <div>

                    <h1
                        style={{
                            fontSize: 36,
                            margin: 0
                        }}
                    >
                        Marketplace
                    </h1>


                    <p
                        style={{
                            color: "#cbd5e1",
                            marginTop: 15,
                            maxWidth: 500,
                            lineHeight: 1.7
                        }}
                    >
                        Discover, publish and sell products
                        from your brand. Manage your live
                        marketplace listings, featured products
                        and product visibility from one place.
                    </p>


                    <button
                        onClick={
                            handleAddProduct
                        }
                        style={{
                            marginTop: 25,
                            background: "#2563eb",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            padding: "12px 24px",
                            cursor: "pointer",
                            fontWeight: 600
                        }}
                    >
                        + Add Product
                    </button>

                </div>


                <div
                    style={{
                        textAlign: "right"
                    }}
                >

                    <h2
                        style={{
                            margin: 0,
                            fontSize: 42
                        }}
                    >
                        {products.length}
                    </h2>


                    <div
                        style={{
                            color: "#cbd5e1"
                        }}
                    >
                        Live Products
                    </div>

                </div>

            </div>


            {/* ==================================================
                ERROR
            ================================================== */}

            {error && (

                <div
                    style={{
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        color: "#991b1b",
                        padding: 15,
                        borderRadius: 10,
                        marginBottom: 25
                    }}
                >
                    {error}
                </div>

            )}


            {/* ==================================================
                STATS
            ================================================== */}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns:
                        "repeat(auto-fit,minmax(180px,1fr))",
                    gap: 20,
                    marginBottom: 35
                }}
            >

                {[
                    [
                        "Live Products",
                        products.length
                    ],

                    [
                        "Featured",
                        featuredCount
                    ],

                    [
                        "Products Sold",
                        totalSold
                    ],

                    [
                        "Visitors",
                        totalViews
                    ]

                ].map(card => (

                    <div
                        key={card[0]}
                        style={{
                            background: "#fff",
                            borderRadius: 15,
                            padding: 25,
                            boxShadow:
                                "0 5px 18px rgba(0,0,0,.06)"
                        }}
                    >

                        <div
                            style={{
                                color: "#888"
                            }}
                        >
                            {card[0]}
                        </div>


                        <h2
                            style={{
                                marginTop: 10,
                                marginBottom: 0
                            }}
                        >
                            {card[1]}
                        </h2>

                    </div>

                ))}

            </div>


            {/* ==================================================
                FILTERS
            ================================================== */}

            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 15,
                    marginBottom: 25,
                    flexWrap: "wrap"
                }}
            >

                <input
                    value={search}
                    onChange={
                        e =>
                            setSearch(
                                e.target.value
                            )
                    }
                    placeholder="Search products..."
                    style={{
                        width: 350,
                        maxWidth: "100%",
                        padding: 12,
                        borderRadius: 8,
                        border: "1px solid #ddd",
                        boxSizing: "border-box"
                    }}
                />


                <select
                    value={sort}
                    onChange={
                        e =>
                            setSort(
                                e.target.value
                            )
                    }
                    style={{
                        padding: 12,
                        borderRadius: 8,
                        border: "1px solid #ddd",
                        background: "#fff"
                    }}
                >

                    <option value="newest">
                        Newest
                    </option>

                    <option value="popular">
                        Most Popular
                    </option>

                    <option value="highest">
                        Highest Price
                    </option>

                    <option value="lowest">
                        Lowest Price
                    </option>

                </select>

            </div>


            {/* ==================================================
                LOADING
            ================================================== */}

            {loading && (

                <div
                    style={{
                        background: "#fff",
                        borderRadius: 16,
                        padding: 50,
                        textAlign: "center",
                        color: "#666"
                    }}
                >
                    Loading marketplace products...
                </div>

            )}


            {/* ==================================================
                EMPTY
            ================================================== */}

            {!loading &&
                filteredProducts.length === 0 && (

                    <div
                        style={{
                            background: "#fff",
                            borderRadius: 16,
                            padding: 60,
                            textAlign: "center",
                            color: "#666"
                        }}
                    >

                        <h3
                            style={{
                                marginTop: 0,
                                color: "#222"
                            }}
                        >
                            {search
                                ? "No products found"
                                : "No live products yet"
                            }
                        </h3>


                        <p>
                            {search
                                ? "Try another search term."
                                : "Publish a product from Product Management and it will appear here."
                            }
                        </p>


                        {!search && (

                            <button
                                onClick={
                                    handleAddProduct
                                }
                                style={{
                                    marginTop: 15,
                                    background: "#2563eb",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    padding: "11px 20px",
                                    cursor: "pointer",
                                    fontWeight: 600
                                }}
                            >
                                + Add Product
                            </button>

                        )}

                    </div>

                )}


            {/* ==================================================
                PRODUCT GRID
            ================================================== */}

            {!loading &&
                filteredProducts.length > 0 && (

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fill,minmax(260px,1fr))",
                            gap: 25
                        }}
                    >

                        {filteredProducts.map(
                            product => {

                                const image =
                                    getProductImage(
                                        product
                                    );


                                return (

                                    <div
                                        key={product.id}
                                        style={{
                                            background: "#fff",
                                            borderRadius: 16,
                                            overflow: "hidden",
                                            boxShadow:
                                                "0 8px 20px rgba(0,0,0,.05)",
                                            position: "relative"
                                        }}
                                    >

                                        {/* Featured */}

                                        {product.featured && (

                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: 12,
                                                    left: 12,
                                                    zIndex: 2,
                                                    background:
                                                        "rgb(255,0,204)",
                                                    color: "#fff",
                                                    padding:
                                                        "6px 10px",
                                                    borderRadius: 20,
                                                    fontSize: 12,
                                                    fontWeight: 700
                                                }}
                                            >
                                                Featured
                                            </div>

                                        )}


                                        {/* Product Image */}

                                        <div
                                            style={{
                                                width: "100%",
                                                height: 220,
                                                background: "#f1f5f9"
                                            }}
                                        >

                                            {image ? (

                                                <img
                                                    src={image}
                                                    alt={
                                                        product.title ||
                                                        "Product"
                                                    }
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover"
                                                    }}
                                                />

                                            ) : (

                                                <div
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: "#94a3b8",
                                                        fontSize: 14
                                                    }}
                                                >
                                                    No image
                                                </div>

                                            )}

                                        </div>


                                        {/* Product Information */}

                                        <div
                                            style={{
                                                padding: 20
                                            }}
                                        >

                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: "#888",
                                                    marginBottom: 7
                                                }}
                                            >
                                                {product.category ||
                                                    "Product"}
                                            </div>


                                            <h3
                                                style={{
                                                    margin: 0,
                                                    lineHeight: 1.3
                                                }}
                                            >
                                                {product.title ||
                                                    "Untitled Product"}
                                            </h3>


                                            <p
                                                style={{
                                                    color: "#666",
                                                    marginTop: 10,
                                                    lineHeight: 1.5,
                                                    minHeight: 45
                                                }}
                                            >
                                                {product.shortDescription ||
                                                    product.description ||
                                                    "No product description available."}
                                            </p>


                                            {/* Rating */}

                                            {Number(
                                                product.rating || 0
                                            ) > 0 && (

                                                <div
                                                    style={{
                                                        marginTop: 10,
                                                        fontSize: 13,
                                                        color: "#666"
                                                    }}
                                                >

                                                    ★{" "}
                                                    {Number(
                                                        product.rating
                                                    ).toFixed(1)}

                                                    {" "}

                                                    (
                                                    {Number(
                                                        product.reviewCount ||
                                                        0
                                                    )}
                                                    )

                                                </div>

                                            )}


                                            {/* Price */}

                                            <div
                                                style={{
                                                    marginTop: 18,
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems: "center",
                                                    gap: 10
                                                }}
                                            >

                                                <div>

                                                    <strong
                                                        style={{
                                                            fontSize: 20
                                                        }}
                                                    >
                                                        {formatPrice(
                                                            product.price,
                                                            product.currency
                                                        )}
                                                    </strong>


                                                    {Number(
                                                        product.comparePrice ||
                                                        0
                                                    ) > 0 && (

                                                        <div
                                                            style={{
                                                                fontSize: 12,
                                                                color: "#999",
                                                                textDecoration:
                                                                    "line-through",
                                                                marginTop: 3
                                                            }}
                                                        >
                                                            {formatPrice(
                                                                product.comparePrice,
                                                                product.currency
                                                            )}
                                                        </div>

                                                    )}

                                                </div>


                                                <button
                                                    onClick={() =>
                                                        handleViewProduct(
                                                            product
                                                        )
                                                    }
                                                    style={{
                                                        background:
                                                            "#2563eb",
                                                        color: "#fff",
                                                        border: "none",
                                                        borderRadius: 8,
                                                        padding:
                                                            "10px 18px",
                                                        cursor: "pointer"
                                                    }}
                                                >
                                                    View
                                                </button>

                                            </div>

                                        </div>

                                    </div>

                                );

                            }
                        )}

                    </div>

                )}

        </div>

    );

}